import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { ClientVisitEntity } from './client-visit.entity';
import { ClientEntity } from './client.entity';
import { MSmClientOpportunityEntity } from './m-sm-client-opportunity.entity';

@Index('client_note__fk_client__id_fk', ['fkClientId'], {})
@Index('client_note__fk_m_sm_client_visit__id_fk', ['fkClientVisitId'], {})
@Index('client_note_externalId_uindex', ['deletedUserId', 'externalId'], {
	unique: true,
})
@Index(
	'm_sm_fk_m_sm_client_opportunity__id',
	['mSmFkMSmClientOpportunityId'],
	{},
)
@Entity('client_note')
export class ClientNoteEntity extends BaseEntity {
	@Column('int', { name: '_fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('text', { name: 'text', nullable: true })
	public text!: string | null;

	@Column('varchar', { name: 'externalId', nullable: true, length: 255 })
	public externalId!: string | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('int', {
		name: 'm_sm_fk_m_sm_client_opportunity__id',
		nullable: true,
		unsigned: true,
	})
	public mSmFkMSmClientOpportunityId!: number | null;

	/**
	 * PD-14327 — the sales visit this note was logged from, when it came from
	 * the Log Visit flow. The note deliberately stays on `client_note` (rather
	 * than moving onto the visit row) so it keeps appearing in the client's
	 * notes timeline; this column only records where it came from, so the
	 * visit surfaces can show it alongside the visit.
	 *
	 * Nullable, and ON DELETE SET NULL: notes typed straight into the client's
	 * timeline have no visit, and removing a visit must leave its note on the
	 * client rather than taking it with it. Visits are soft-removed, so in
	 * practice the link survives a delete and the note simply keeps showing on
	 * the client — the SET NULL only matters if a visit row is ever purged.
	 *
	 * Existing notes are NOT backfilled: a historical note carries nothing that
	 * reliably identifies which historical visit produced it, so guessing would
	 * attach notes to the wrong visits.
	 */
	@Column('int', {
		name: 'fk_m_sm_client_visit__id',
		nullable: true,
		unsigned: true,
	})
	public fkClientVisitId!: number | null;

	@Column('datetime', { name: 'dateTime', default: () => 'CURRENT_TIMESTAMP' })
	public dateTime!: Date;

	@Column('tinyint', { name: 'isPinned', default: () => "'0'", width: 1 })
	public isPinned!: number;

	@OneToMany(() => ClientEntity, (client) => client.fkClientNote, {
		lazy: true,
	})
	public clients!: Promise<ClientEntity[]>;

	@ManyToOne(
		() => MSmClientOpportunityEntity,
		(mSmClientOpportunity) => mSmClientOpportunity.clientNotes,
		{ onDelete: 'RESTRICT', onUpdate: 'RESTRICT' },
	)
	@JoinColumn([
		{ name: 'm_sm_fk_m_sm_client_opportunity__id', referencedColumnName: 'id' },
	])
	public mSmFkMSmClientOpportunity!: MSmClientOpportunityEntity;

	@ManyToOne(() => ClientEntity, (client) => client.clientNotes, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity;

	@ManyToOne(
		() => ClientVisitEntity,
		(clientVisit) => clientVisit.clientNotes,
		{
			onDelete: 'SET NULL',
			onUpdate: 'RESTRICT',
			nullable: true,
		},
	)
	@JoinColumn([
		{ name: 'fk_m_sm_client_visit__id', referencedColumnName: 'id' },
	])
	public fkClientVisit!: ClientVisitEntity | null;
}
